import { useRef, useState, useEffect, useCallback } from "react";

import { useId } from "./useId";

export const usePopup = () => {
  const popupRef = useRef<Window | null>(null);
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(
    ({ url, width = 500, height = 600 }: { url: string; width?: number; height?: number }) => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.focus();
        return;
      }

      const left = window.screenX + (window.innerWidth - width) / 2;
      const top = window.screenY + (window.innerHeight - height) / 2;

      popupRef.current = window.open(
        url,
        id,
        `width=${width},height=${height},top=${top},left=${left},resizable=no,scrollbars=no`
      );

      if (!popupRef.current) {
        throw new Error(`Couldn't open popup with url: ${url}`);
      }

      setIsOpen(true);
    },
    [id]
  );

  const close = useCallback(() => {
    if (popupRef.current) {
      popupRef.current.close();
    }

    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const interval = setInterval(() => {
      try {
        if (popupRef.current?.closed) {
          setIsOpen(false);
        }
      } catch {
        // Cross-origin popup - we can't access the closed property
        // This typically happens with OAuth flows to external domains
        // The popup will clean up on unmount, but we can't track its state
        console.warn("Unable to check popup status due to cross-origin policy");
      }
    }, 500);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen]);

  useEffect(() => {
    return () => {
      popupRef.current?.close();
    };
  }, []);

  return { open, close, isOpen, popupRef };
};
